import { handleKeyDown } from "modules/common/utils/keyboardUtils";
import { ReactNode, useContext, useRef, useState } from "react";
import Select, { OptionTypeBase, StylesConfig } from "react-select";
import { Props as SelectComponentsProps } from "react-select/src/Select";
import { AccessibilityContext } from "views/AccessibilityContext";
import { Control } from "./Control";
import { DropdownIndicator } from "./DropdownIndicator";

export interface OptionType<T = string> extends OptionTypeBase {
    value: T;
    label: string;
}

export interface DropdownProps<T> extends SelectComponentsProps<OptionType<T>> {
    maxMenuHeight: number;
    size: "large" | "medium" | "small";
    errorMessage?: ReactNode;
    menuPlacement?: "auto" | "bottom" | "top";
}

const getStyles = (lowVision?: boolean): StylesConfig<any, any> => ({
    valueContainer: provided => ({ ...provided, padding: 0, position: "relative" }),
    placeholder: provided => ({
        ...provided,
        color: getComputedStyle(document.documentElement).getPropertyValue(
            lowVision ? "--color-text-white" : "--color-secondary-black-gray"
        ),
        position: "relative",
    }),
    singleValue: provided =>
        lowVision
            ? {
                  ...provided,
                  color: getComputedStyle(document.documentElement).getPropertyValue(
                      "--color-text-white"
                  ),
              }
            : provided,
    menuPortal: provided => ({ ...provided, zIndex: 9999 }),
    menu: provided => ({
        ...provided,
        padding: 0,
        margin: 0,
        position: "absolute",
        borderRadius: getComputedStyle(document.documentElement).getPropertyValue(
            "--border-radius"
        ),
        zIndex: 9999,
        backgroundColor: lowVision
            ? getComputedStyle(document.documentElement).getPropertyValue("--black")
            : provided.backgroundColor,
    }),
    groupHeading: provided => ({ ...provided, padding: 0, margin: 0 }),
    group: provided => ({ ...provided, margin: 0 }),
    option: (provided, state) =>
        lowVision
            ? {
                  ...provided,
                  margin: 0,
                  backgroundColor: state.isFocused
                      ? getComputedStyle(document.documentElement).getPropertyValue("--lightgrey")
                      : getComputedStyle(document.documentElement).getPropertyValue("--black"),
                  color: getComputedStyle(document.documentElement).getPropertyValue(
                      "--color-background"
                  ),
              }
            : { ...provided, margin: 0 },
    input: provided => ({
        ...provided,
        margin: 0,
        padding: 0,
        border: 0,
        caretColor: "transparent",
    }),
    dropdownIndicator: provided => ({ ...provided, padding: 0 }),
});

const combineStyles = (
    customStyles?: Partial<StylesConfig<any, any, any>>,
    lowVision?: boolean
): StylesConfig<any, any, any> => {
    const baseStyles = getStyles(lowVision);
    return Object.keys(baseStyles).reduce((acc, key) => {
        acc[key as keyof typeof baseStyles] = (provided: any, state: any) => {
            const base = baseStyles[key as keyof typeof baseStyles];
            const custom = customStyles?.[key as keyof typeof baseStyles];
            return { ...(base?.(provided, state) ?? {}), ...(custom?.(provided, state) ?? {}) };
        };
        return acc;
    }, {} as StylesConfig<any, any>);
};

const CustomControl = (props: any) => {
    return (
        <Control
            {...props}
            innerProps={{
                ...props.innerProps,
                tabIndex: props.selectProps.tabIndex ?? 0,
            }}
        />
    );
};

export const Dropdown = <T,>(props: DropdownProps<T>) => {
    const { lowVision } = useContext(AccessibilityContext);
    const selectRef = useRef<any>(null);
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <>
            <Select<OptionType<T>>
                {...props}
                maxMenuHeight={props.maxMenuHeight}
                menuPlacement={props.menuPlacement ?? "bottom"}
                noOptionsMessage={props.noOptionsMessage ?? (() => "Nema podataka")}
                styles={combineStyles(props.styles, lowVision)}
                components={{
                    Control: CustomControl,
                    DropdownIndicator,
                    IndicatorSeparator: () => null,
                }}
                ref={selectRef}
                tabIndex={props.tabIndex}
                menuIsOpen={menuOpen}
                onMenuOpen={() => setMenuOpen(true)}
                onMenuClose={() => setMenuOpen(false)}
                onKeyDown={e => handleKeyDown(e, () => setMenuOpen(prev => !prev))}
            />
            {props.errorMessage && <div className="dropdown-error">{props.errorMessage}</div>}
        </>
    );
};
